/**
 * One-shot Web Audio SFX. The music itself is now an audio file streamed
 * by AudioEngine; only the gameplay feedback layer (hit clicks, whooshes,
 * end-of-run stings) remains procedurally generated. These are short and
 * predictable so a tiny synth gives much better perceived responsiveness
 * than buffer playback would.
 *
 * Output level is pre-trimmed by SFX_TRIM so the SFX bus volume slider in
 * volumePrefs scales it linearly without compounding gains.
 */

const SFX_TRIM = 0.7;

export function midiToHz(midi: number): number {
  return 440 * Math.pow(2, (midi - 69) / 12);
}

/**
 * C major pentatonic ascending across two octaves. Combo streaks step
 * through this lookup so a sustained perfect run sings up the scale
 * instead of repeating one chirp; the index resets to 0 on combo
 * break (handled by the caller). Picked to be pleasing against any
 * tonal centre the underlying track might sit on — pentatonic
 * intervals dodge the leading-tone clashes that diatonic scales risk.
 */
const PENTATONIC_MIDI: ReadonlyArray<number> = [72, 74, 76, 79, 81, 84, 86, 88];

export const COMBO_PENTATONIC_LENGTH = PENTATONIC_MIDI.length;

export interface HitClickOptions {
  /** Stereo pan position in [-1, 1]; defaults to 0 (centred). */
  pan?: number;
  /**
   * Index into the pentatonic combo scale (0 .. COMBO_PENTATONIC_LENGTH-1).
   * Caller advances on every successful hit and resets on combo
   * break. Ignored for `miss` (a melodic top would feel rewarding,
   * which is the opposite of the intent).
   */
  comboNote?: number;
}

/**
 * Build a stereo-panned destination, falling back to the bare destination
 * when {@link AudioContext.createStereoPanner} is unavailable (older
 * Safari). Returns the head of the chain to connect into and the tail
 * already connected to `destination`.
 */
function panDestination(
  ctx: AudioContext,
  destination: AudioNode,
  pan: number,
): AudioNode {
  if (typeof ctx.createStereoPanner !== 'function') {
    return destination;
  }
  const panner = ctx.createStereoPanner();
  panner.pan.value = Math.max(-1, Math.min(1, pan));
  panner.connect(destination);
  return panner;
}

/**
 * Layered hit click. Built from up to four stacked layers so hits read
 * as WEIGHT, not chirp:
 *   - body:     low percussive thump (~80-110 Hz, 35 ms) — the pluck
 *   - click:    bandpassed noise transient (~5 ms)        — the attack
 *   - sub:      60 Hz sine punch (perfect only, ~12 ms)   — the impact
 *   - sparkle:  pentatonic top note keyed off comboNote   — the melody
 *
 * The sparkle's pitch follows {@link HitClickOptions.comboNote} so a
 * stream of perfects walks up a pentatonic scale; the caller resets
 * the counter on combo break. Pan is applied via a single
 * StereoPannerNode at the output of the layered mix.
 */
export function scheduleHitClick(
  ctx: AudioContext,
  destination: AudioNode,
  t0: number,
  variant: 'perfect' | 'good' | 'ok' | 'miss' = 'perfect',
  opts: HitClickOptions = {},
): void {
  const out = panDestination(ctx, destination, opts.pan ?? 0);
  if (variant === 'miss') {
    scheduleMissLayer(ctx, out, t0);
    return;
  }
  scheduleBodyLayer(ctx, out, t0, variant);
  scheduleClickTransient(ctx, out, t0, variant);
  scheduleSparkleLayer(ctx, out, t0, variant, opts.comboNote ?? 0);
  if (variant === 'perfect') {
    scheduleSubBassThump(ctx, out, t0);
  }
}

/**
 * Filtered noise + low oscillator describing a soft "thunk". The
 * existing miss profile was already serviceable so we re-tuned rather
 * than rebuilt — it feels distinctly worse than a hit (lower pitch,
 * longer tail) without becoming punishing to listen to on repeat.
 */
function scheduleMissLayer(ctx: AudioContext, destination: AudioNode, t0: number): void {
  const osc = ctx.createOscillator();
  osc.type = 'sine';
  osc.frequency.setValueAtTime(160, t0);
  osc.frequency.exponentialRampToValueAtTime(55, t0 + 0.12);
  const gain = ctx.createGain();
  gain.gain.setValueAtTime(0.42 * SFX_TRIM, t0);
  gain.gain.exponentialRampToValueAtTime(0.001, t0 + 0.16);
  osc.connect(gain).connect(destination);
  osc.start(t0);
  osc.stop(t0 + 0.18);

  const noiseDur = 0.06;
  const buffer = ctx.createBuffer(1, Math.ceil(ctx.sampleRate * noiseDur), ctx.sampleRate);
  const data = buffer.getChannelData(0);
  for (let i = 0; i < data.length; i++) {
    data[i] = (Math.random() * 2 - 1) * (1 - i / data.length);
  }
  const noise = ctx.createBufferSource();
  noise.buffer = buffer;
  const filter = ctx.createBiquadFilter();
  filter.type = 'bandpass';
  filter.frequency.value = 480;
  filter.Q.value = 1.4;
  const nGain = ctx.createGain();
  nGain.gain.setValueAtTime(0.28 * SFX_TRIM, t0);
  nGain.gain.exponentialRampToValueAtTime(0.001, t0 + noiseDur);
  noise.connect(filter).connect(nGain).connect(destination);
  noise.start(t0);
}

function scheduleBodyLayer(
  ctx: AudioContext,
  destination: AudioNode,
  t0: number,
  variant: 'perfect' | 'good' | 'ok',
): void {
  const osc = ctx.createOscillator();
  osc.type = 'sine';
  // OK sits below `good` in both pitch range and amplitude so the
  // mercy-band 50 reads as "you barely caught it" rather than "great
  // hit" — the player should still feel motivated to tighten their
  // timing.
  const startFreq = variant === 'perfect' ? 110 : variant === 'good' ? 95 : 82;
  const endFreq = variant === 'perfect' ? 70 : variant === 'good' ? 65 : 60;
  osc.frequency.setValueAtTime(startFreq, t0);
  osc.frequency.exponentialRampToValueAtTime(endFreq, t0 + 0.035);
  const gain = ctx.createGain();
  // Snap up over 2 ms so the attack registers without a click, then
  // exp ramp out — gives the body a percussive "tom" feel rather than
  // the previous pure tonal sweep.
  const peak = variant === 'perfect' ? 0.55 : variant === 'good' ? 0.42 : 0.30;
  gain.gain.setValueAtTime(0, t0);
  gain.gain.linearRampToValueAtTime(peak * SFX_TRIM, t0 + 0.002);
  gain.gain.exponentialRampToValueAtTime(0.001, t0 + 0.05);
  osc.connect(gain).connect(destination);
  osc.start(t0);
  osc.stop(t0 + 0.06);
}

/**
 * Bright noise burst at the very start of the hit, ~5 ms long. Sells
 * the "stick on skin" attack the body alone can't articulate.
 */
function scheduleClickTransient(
  ctx: AudioContext,
  destination: AudioNode,
  t0: number,
  variant: 'perfect' | 'good' | 'ok',
): void {
  const dur = 0.008;
  const samples = Math.max(1, Math.ceil(ctx.sampleRate * dur));
  const buffer = ctx.createBuffer(1, samples, ctx.sampleRate);
  const data = buffer.getChannelData(0);
  for (let i = 0; i < data.length; i++) {
    data[i] = (Math.random() * 2 - 1) * (1 - i / data.length);
  }
  const noise = ctx.createBufferSource();
  noise.buffer = buffer;
  const filter = ctx.createBiquadFilter();
  filter.type = 'highpass';
  filter.frequency.value = variant === 'perfect' ? 2200 : variant === 'good' ? 1600 : 1200;
  filter.Q.value = 0.7;
  const gain = ctx.createGain();
  const peak = variant === 'perfect' ? 0.32 : variant === 'good' ? 0.22 : 0.14;
  gain.gain.setValueAtTime(peak * SFX_TRIM, t0);
  gain.gain.exponentialRampToValueAtTime(0.001, t0 + dur);
  noise.connect(filter).connect(gain).connect(destination);
  noise.start(t0);
}

/**
 * Pentatonic top note. Triangle-wave so the harmonics are rich enough
 * to read as melodic without being harsh against music. Two
 * detuned voices stacked for the perfect variant give a tiny chorus
 * shimmer so back-to-back perfects don't sound mechanically identical.
 */
function scheduleSparkleLayer(
  ctx: AudioContext,
  destination: AudioNode,
  t0: number,
  variant: 'perfect' | 'good' | 'ok',
  comboNote: number,
): void {
  const idx = ((comboNote % COMBO_PENTATONIC_LENGTH) + COMBO_PENTATONIC_LENGTH) % COMBO_PENTATONIC_LENGTH;
  const baseHz = midiToHz(PENTATONIC_MIDI[idx]);
  const releaseDur = variant === 'perfect' ? 0.18 : variant === 'good' ? 0.12 : 0.09;
  const peak = variant === 'perfect' ? 0.22 : variant === 'good' ? 0.17 : 0.10;
  const voices = variant === 'perfect' ? [-7, 7] : [0];
  for (const detuneCents of voices) {
    const osc = ctx.createOscillator();
    osc.type = 'triangle';
    osc.frequency.value = baseHz;
    osc.detune.value = detuneCents;
    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0, t0);
    gain.gain.linearRampToValueAtTime((peak / voices.length) * SFX_TRIM, t0 + 0.005);
    gain.gain.exponentialRampToValueAtTime(0.001, t0 + releaseDur);
    osc.connect(gain).connect(destination);
    osc.start(t0);
    osc.stop(t0 + releaseDur + 0.02);
  }
}

/**
 * Sub-bass punch: a single sine cycle at 60 Hz that lands at the same
 * instant as the body. Inaudible on phone speakers but transformative
 * on headphones / desktop subs — this is the layer the original
 * "chirpy" hit was missing.
 */
function scheduleSubBassThump(ctx: AudioContext, destination: AudioNode, t0: number): void {
  const osc = ctx.createOscillator();
  osc.type = 'sine';
  osc.frequency.setValueAtTime(80, t0);
  osc.frequency.exponentialRampToValueAtTime(45, t0 + 0.025);
  const gain = ctx.createGain();
  gain.gain.setValueAtTime(0, t0);
  gain.gain.linearRampToValueAtTime(0.45 * SFX_TRIM, t0 + 0.003);
  gain.gain.exponentialRampToValueAtTime(0.001, t0 + 0.04);
  osc.connect(gain).connect(destination);
  osc.start(t0);
  osc.stop(t0 + 0.05);
}

/**
 * Tiny high-pitched tick used by the score-roll-up animation. Plays
 * every ~100 score units crossed during the ease so the eye-catching
 * scoreboard count-up has an audible tactile partner. Deliberately
 * very quiet so a fast roll doesn't become a hissy stream.
 */
export function scheduleScoreTick(
  ctx: AudioContext,
  destination: AudioNode,
  t0: number,
): void {
  const osc = ctx.createOscillator();
  osc.type = 'triangle';
  osc.frequency.setValueAtTime(2200, t0);
  osc.frequency.exponentialRampToValueAtTime(2700, t0 + 0.025);
  const gain = ctx.createGain();
  gain.gain.setValueAtTime(0, t0);
  gain.gain.linearRampToValueAtTime(0.06 * SFX_TRIM, t0 + 0.003);
  gain.gain.exponentialRampToValueAtTime(0.001, t0 + 0.045);
  osc.connect(gain).connect(destination);
  osc.start(t0);
  osc.stop(t0 + 0.06);
}

export function scheduleFlipWhoosh(
  ctx: AudioContext,
  destination: AudioNode,
  t0: number,
): void {
  const dur = 0.18;
  const buffer = ctx.createBuffer(1, ctx.sampleRate * dur, ctx.sampleRate);
  const data = buffer.getChannelData(0);
  for (let i = 0; i < data.length; i++) {
    data[i] = (Math.random() * 2 - 1) * (1 - i / data.length);
  }
  const source = ctx.createBufferSource();
  source.buffer = buffer;
  const filter = ctx.createBiquadFilter();
  filter.type = 'bandpass';
  filter.frequency.setValueAtTime(420, t0);
  filter.frequency.linearRampToValueAtTime(2800, t0 + dur);
  filter.Q.value = 7;
  const gain = ctx.createGain();
  gain.gain.setValueAtTime(0.45 * SFX_TRIM, t0);
  gain.gain.exponentialRampToValueAtTime(0.001, t0 + dur);
  source.connect(filter).connect(gain).connect(destination);
  source.start(t0);
}

/**
 * Low-frequency boom layered over the regular miss click when a sizeable
 * combo (>= 25) breaks. Sells the "you really lost something" feeling
 * without stomping on the regular miss feedback. Shared across all
 * modes — the heavier Guitar-only multi-miss boom lives in
 * `guitarSfx.ts::scheduleCrashBoom` and is the only one that can layer
 * sawtooth + noise without bleeding into Piano / Circles / Pulse.
 */
export function scheduleComboBreakBoom(
  ctx: AudioContext,
  destination: AudioNode,
  t0: number,
): void {
  const osc = ctx.createOscillator();
  osc.type = 'sine';
  osc.frequency.setValueAtTime(120, t0);
  osc.frequency.exponentialRampToValueAtTime(40, t0 + 0.35);
  const gain = ctx.createGain();
  gain.gain.setValueAtTime(0.55 * SFX_TRIM, t0);
  gain.gain.exponentialRampToValueAtTime(0.001, t0 + 0.4);
  osc.connect(gain).connect(destination);
  osc.start(t0);
  osc.stop(t0 + 0.45);
}

export function scheduleDeathSound(
  ctx: AudioContext,
  destination: AudioNode,
  t0: number,
): void {
  const dur = 0.6;
  const osc = ctx.createOscillator();
  osc.type = 'sawtooth';
  osc.frequency.setValueAtTime(220, t0);
  osc.frequency.exponentialRampToValueAtTime(35, t0 + dur);
  const gain = ctx.createGain();
  gain.gain.setValueAtTime(0.55 * SFX_TRIM, t0);
  gain.gain.exponentialRampToValueAtTime(0.001, t0 + dur);
  const filter = ctx.createBiquadFilter();
  filter.type = 'lowpass';
  filter.frequency.value = 800;
  osc.connect(filter).connect(gain).connect(destination);
  osc.start(t0);
  osc.stop(t0 + dur + 0.05);

  const buffer = ctx.createBuffer(1, ctx.sampleRate * dur, ctx.sampleRate);
  const data = buffer.getChannelData(0);
  for (let i = 0; i < data.length; i++) {
    data[i] = (Math.random() * 2 - 1) * (1 - i / data.length) * 0.6;
  }
  const noise = ctx.createBufferSource();
  noise.buffer = buffer;
  const nGain = ctx.createGain();
  nGain.gain.setValueAtTime(0.32 * SFX_TRIM, t0);
  nGain.gain.exponentialRampToValueAtTime(0.001, t0 + dur);
  noise.connect(nGain).connect(destination);
  noise.start(t0);
}

/**
 * Soft, percussive blip for taps inside a burst chamber. Lower-pitched
 * and quieter than a hit click so a fast mash sounds like a drum roll
 * rather than a flock of perfect clicks.
 */
export function scheduleMashTap(
  ctx: AudioContext,
  destination: AudioNode,
  t0: number,
): void {
  const osc = ctx.createOscillator();
  osc.type = 'square';
  osc.frequency.setValueAtTime(360, t0);
  osc.frequency.exponentialRampToValueAtTime(220, t0 + 0.05);
  const gain = ctx.createGain();
  gain.gain.setValueAtTime(0.32 * SFX_TRIM, t0);
  gain.gain.exponentialRampToValueAtTime(0.001, t0 + 0.06);
  osc.connect(gain).connect(destination);
  osc.start(t0);
  osc.stop(t0 + 0.07);
}

/** Short ascending blip when the player starts climbing in Waves mode. */
export function scheduleWavesThrust(
  ctx: AudioContext,
  destination: AudioNode,
  t0: number,
): void {
  const osc = ctx.createOscillator();
  osc.type = 'sine';
  osc.frequency.setValueAtTime(240, t0);
  osc.frequency.exponentialRampToValueAtTime(680, t0 + 0.07);
  const gain = ctx.createGain();
  gain.gain.setValueAtTime(0, t0);
  gain.gain.linearRampToValueAtTime(0.22 * SFX_TRIM, t0 + 0.008);
  gain.gain.exponentialRampToValueAtTime(0.001, t0 + 0.09);
  osc.connect(gain).connect(destination);
  osc.start(t0);
  osc.stop(t0 + 0.1);
}

/** Short descending blip when the player starts diving in Waves mode. */
export function scheduleWavesDive(
  ctx: AudioContext,
  destination: AudioNode,
  t0: number,
): void {
  const osc = ctx.createOscillator();
  osc.type = 'sine';
  osc.frequency.setValueAtTime(620, t0);
  osc.frequency.exponentialRampToValueAtTime(180, t0 + 0.07);
  const gain = ctx.createGain();
  gain.gain.setValueAtTime(0, t0);
  gain.gain.linearRampToValueAtTime(0.2 * SFX_TRIM, t0 + 0.008);
  gain.gain.exponentialRampToValueAtTime(0.001, t0 + 0.09);
  osc.connect(gain).connect(destination);
  osc.start(t0);
  osc.stop(t0 + 0.1);
}

export function scheduleLeadInTick(
  ctx: AudioContext,
  destination: AudioNode,
  t0: number,
  isFinal: boolean,
): void {
  const osc = ctx.createOscillator();
  osc.type = 'square';
  osc.frequency.value = isFinal ? 1500 : 880;
  const gain = ctx.createGain();
  gain.gain.setValueAtTime(0, t0);
  gain.gain.linearRampToValueAtTime(0.4 * SFX_TRIM, t0 + 0.005);
  gain.gain.exponentialRampToValueAtTime(0.001, t0 + (isFinal ? 0.18 : 0.06));
  osc.connect(gain).connect(destination);
  osc.start(t0);
  osc.stop(t0 + 0.2);
}

export function scheduleComboCelebration(
  ctx: AudioContext,
  destination: AudioNode,
  t0: number,
): void {
  const notes = [60, 64, 67, 72];
  for (let i = 0; i < notes.length; i++) {
    const freq = midiToHz(notes[i]);
    const osc = ctx.createOscillator();
    osc.type = 'triangle';
    osc.frequency.value = freq;
    const gain = ctx.createGain();
    const start = t0 + i * 0.04;
    gain.gain.setValueAtTime(0, start);
    gain.gain.linearRampToValueAtTime(0.32 * SFX_TRIM, start + 0.01);
    gain.gain.exponentialRampToValueAtTime(0.001, start + 0.18);
    osc.connect(gain).connect(destination);
    osc.start(start);
    osc.stop(start + 0.2);
  }
}

export function scheduleVictorySting(
  ctx: AudioContext,
  destination: AudioNode,
  t0: number,
): void {
  const arp = [60, 64, 67, 72, 76];
  for (let i = 0; i < arp.length; i++) {
    const freq = midiToHz(arp[i]);
    const osc = ctx.createOscillator();
    osc.type = 'triangle';
    osc.frequency.value = freq;
    const gain = ctx.createGain();
    const start = t0 + i * 0.06;
    gain.gain.setValueAtTime(0, start);
    gain.gain.linearRampToValueAtTime(0.4 * SFX_TRIM, start + 0.012);
    gain.gain.exponentialRampToValueAtTime(0.001, start + 0.5);
    osc.connect(gain).connect(destination);
    osc.start(start);
    osc.stop(start + 0.55);
  }
  for (const n of [60, 67, 72]) {
    const osc = ctx.createOscillator();
    osc.type = 'sawtooth';
    osc.frequency.value = midiToHz(n);
    const filter = ctx.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.value = 1400;
    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0, t0);
    gain.gain.linearRampToValueAtTime(0.18 * SFX_TRIM, t0 + 0.18);
    gain.gain.setValueAtTime(0.18 * SFX_TRIM, t0 + 0.8);
    gain.gain.exponentialRampToValueAtTime(0.001, t0 + 1.4);
    osc.connect(filter).connect(gain).connect(destination);
    osc.start(t0);
    osc.stop(t0 + 1.45);
  }
}

/**
 * Milestone fanfare: a short ascending arpeggio with a ringing bell
 * on top. Used for mid-run star/cup celebrations (star_1, star_2,
 * cup_1, cup_2). Tier-aware so the second milestone sounds slightly
 * richer than the first; the loop-1 finale (star_3) uses
 * {@link scheduleVictorySting} alongside its splash overlay.
 */
export function scheduleMilestoneFanfare(
  ctx: AudioContext,
  destination: AudioNode,
  t0: number,
  tier: 1 | 2 | 3,
): void {
  // C major arpeggio rising; the higher the tier, the fuller the chord.
  const notes = tier === 1 ? [60, 64, 67] : tier === 2 ? [60, 64, 67, 72] : [60, 64, 67, 72, 76];
  for (let i = 0; i < notes.length; i++) {
    const osc = ctx.createOscillator();
    osc.type = 'triangle';
    osc.frequency.value = midiToHz(notes[i]);
    const gain = ctx.createGain();
    const start = t0 + i * 0.045;
    gain.gain.setValueAtTime(0, start);
    gain.gain.linearRampToValueAtTime(0.36 * SFX_TRIM, start + 0.01);
    gain.gain.exponentialRampToValueAtTime(0.001, start + 0.32);
    osc.connect(gain).connect(destination);
    osc.start(start);
    osc.stop(start + 0.34);
  }
  // Bell on top to make it feel like a "ding!" earned-reward moment.
  const bell = ctx.createOscillator();
  bell.type = 'sine';
  bell.frequency.value = midiToHz(72 + (tier - 1) * 2);
  const bellGain = ctx.createGain();
  bellGain.gain.setValueAtTime(0, t0);
  bellGain.gain.linearRampToValueAtTime(0.32 * SFX_TRIM, t0 + 0.01);
  bellGain.gain.exponentialRampToValueAtTime(0.001, t0 + 0.55);
  bell.connect(bellGain).connect(destination);
  bell.start(t0);
  bell.stop(t0 + 0.6);
}

export function scheduleFailureSting(
  ctx: AudioContext,
  destination: AudioNode,
  t0: number,
): void {
  for (const n of [60, 63, 65]) {
    const osc = ctx.createOscillator();
    osc.type = 'sawtooth';
    osc.frequency.value = midiToHz(n);
    const filter = ctx.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.setValueAtTime(1200, t0);
    filter.frequency.exponentialRampToValueAtTime(300, t0 + 0.5);
    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0.22 * SFX_TRIM, t0);
    gain.gain.exponentialRampToValueAtTime(0.001, t0 + 0.6);
    osc.connect(filter).connect(gain).connect(destination);
    osc.start(t0);
    osc.stop(t0 + 0.65);
  }
}
